import { isCancel } from 'axios'
import { isHttpClientError } from '@deepcloud/api-http-client'
import { useDeepBoxAdminStore } from '@/stores/deepbox/admin/admin.ts'
import { useApi } from '@/composables/api.ts'
import { useBoxHiddenNodes } from '@/composables/box/box-hidden-nodes.ts'
import type { SearchData } from '@deepcloud/api-deep-box'

interface SearchParamsPayload {
  nodeId: string
  query?: string
  offset?: number
  limit?: number
}

export const useDeepBoxSearchStore = defineStore('deepBoxSearch', () => {
  const fetchSearchResultsPending = ref(false)
  const fetchNextSearchResultsPending = ref(false)
  const isSearchNavigationPending = ref(false)
  const searchResults = ref<SearchData | undefined>(undefined)
  const lastSearchParamsPayload = ref<SearchParamsPayload | undefined>(
    undefined,
  )

  const { deepBoxAPI } = useApi()
  const deepBoxAdminStore = useDeepBoxAdminStore()
  const { canShowHiddenNodes } = useBoxHiddenNodes(
    computed(() => deepBoxAdminStore.adminBox),
  )

  let abortController: AbortController | undefined
  // only the most recently started request may write results or pending flags
  let latestRequestId = 0

  function startRequest() {
    abortController?.abort()
    abortController = new AbortController()
    // the aborted request is no longer the latest, so it can't clear its own flag
    fetchSearchResultsPending.value = false
    fetchNextSearchResultsPending.value = false
    return { id: ++latestRequestId, signal: abortController.signal }
  }

  function isLatest(id: number) {
    return id === latestRequestId
  }

  // the http client wraps axios errors, so the CanceledError sits in `cause`
  function isCancelled(error: unknown) {
    return (
      isCancel(error) || (isHttpClientError(error) && isCancel(error.cause))
    )
  }

  function abortPendingSearch() {
    abortController?.abort()
    abortController = undefined
    latestRequestId++
    fetchSearchResultsPending.value = false
    fetchNextSearchResultsPending.value = false
  }

  function buildSearchQueryParams(payload: SearchParamsPayload) {
    return {
      parentNodeId: payload.nodeId,
      q: payload.query,
      offset: payload.offset,
      limit: payload.limit,
      ...(canShowHiddenNodes.value ? { includeHidden: true } : {}),
    }
  }

  async function fetchSearchResults(payload: SearchParamsPayload) {
    const { id, signal } = startRequest()

    fetchSearchResultsPending.value = true
    lastSearchParamsPayload.value = { ...payload }
    try {
      const res = await deepBoxAPI.value.search.get(
        buildSearchQueryParams(payload),
        { signal },
      )
      if (!isLatest(id)) return undefined
      searchResults.value = { ...res.data }
      return res
    } catch (error) {
      if (isCancelled(error)) return undefined
      throw error
    } finally {
      if (isLatest(id)) {
        fetchSearchResultsPending.value = false
      }
    }
  }

  async function fetchNextSearchResults(payload: SearchParamsPayload) {
    // a page request must never cancel a fresh search
    if (fetchSearchResultsPending.value) return undefined

    const { id, signal } = startRequest()

    fetchNextSearchResultsPending.value = true
    try {
      const res = await deepBoxAPI.value.search.get(
        buildSearchQueryParams(payload),
        { signal },
      )
      if (!isLatest(id)) return undefined
      const searchResultsItems = searchResults.value?.items || []
      searchResults.value = {
        ...res.data,
        items: [...searchResultsItems, ...res.data.items],
      }
      return res
    } catch (error) {
      if (isCancelled(error)) return undefined
      throw error
    } finally {
      if (isLatest(id)) {
        fetchNextSearchResultsPending.value = false
      }
    }
  }

  async function refresh() {
    if (lastSearchParamsPayload.value) {
      await fetchSearchResults(lastSearchParamsPayload.value)
    }
  }

  return {
    // state
    lastSearchParamsPayload,
    fetchSearchResultsPending,
    fetchNextSearchResultsPending,
    isSearchNavigationPending,
    searchResults,
    // actions
    fetchSearchResults,
    fetchNextSearchResults,
    refresh,
    abortPendingSearch,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useDeepBoxSearchStore, import.meta.hot),
  )
}
